/**
 * /admin/rule-reviews — Admin view of the Rule Review Console.
 *
 * Shows:
 *   1. Consensus / discrepancy dashboard — per-rule vote counts
 *   2. Resolve discrepancy modal — writes back rule status/severity
 *   3. Contribution ledger — per-reviewer activity
 *   4. Invite reviewer — email + discipline tags
 *
 * Access: admin / super_admin only.
 */

import * as React from 'react';
import { Navigate } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  ShieldAlert,
  UserPlus,
  X,
} from 'lucide-react';

import useAuthStore from '../stores/authStore';
import { RuleReviewAPI } from '../lib/ruleReviewApi';
import { cn, getErrorMessage } from '../lib/utils';
import type {
  ConsensusSummary,
  LedgerEntry,
  ReviewDiscipline,
} from '../types/ruleReview';
import { DISCIPLINE_LABELS } from '../types/ruleReview';

const ADMIN_ROLES = ['admin', 'super_admin'];
const VALID_DISCIPLINES: ReviewDiscipline[] = ['legal', 'coverage', 'trade', 'estimating'];
const VALID_STATUSES = ['draft', 'proposed', 'active', 'retired'];
const VALID_SEVERITIES = ['must', 'should', 'suggested'];

// ---------------------------------------------------------------------------
// Resolve Modal
// ---------------------------------------------------------------------------

interface ResolveModalProps {
  item: ConsensusSummary;
  onClose: () => void;
  onResolved: (ruleId: string) => void;
}

const ResolveModal: React.FC<ResolveModalProps> = ({ item, onClose, onResolved }) => {
  const [resolution, setResolution] = React.useState('');
  const [newStatus, setNewStatus] = React.useState('');
  const [newSeverity, setNewSeverity] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolution.trim()) {
      setError('Please provide a resolution note.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await RuleReviewAPI.adminResolveDiscrepancy(item.rule_id, {
        resolution,
        new_status: newStatus || undefined,
        new_severity: newSeverity || undefined,
      });
      onResolved(item.rule_id);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-lg bg-card shadow-xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-base font-semibold">Resolve discrepancy</h2>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{item.rule_name}</p>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-muted-foreground">
              {item.agree_count} agree · {item.contest_count} contest ·{' '}
              {item.needs_discussion_count} needs discussion
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Resolution note <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              rows={3}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Explain the decision and rationale…"
              className="w-full rounded border border-input px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Update status (optional)
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— no change —</option>
                {VALID_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Update severity (optional)
              </label>
              <select
                value={newSeverity}
                onChange={(e) => setNewSeverity(e.target.value)}
                className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— no change —</option>
                {VALID_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Resolve'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Invite form
// ---------------------------------------------------------------------------

const InviteReviewerForm: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [disciplines, setDisciplines] = React.useState<ReviewDiscipline[]>([]);
  const [status, setStatus] = React.useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [message, setMessage] = React.useState('');

  const toggleDiscipline = (d: ReviewDiscipline) => {
    setDisciplines((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || disciplines.length === 0) {
      setStatus('error');
      setMessage('Email and at least one discipline are required.');
      return;
    }
    setStatus('saving');
    try {
      const res = await RuleReviewAPI.adminInviteReviewer({ email: email.trim(), disciplines });
      setStatus('done');
      setMessage(`Reviewer role granted to ${res.email}.`);
      setEmail('');
      setDisciplines([]);
    } catch (err) {
      setStatus('error');
      setMessage(getErrorMessage(err));
    }
  };

  return (
    <section className="rounded-lg border bg-card p-5 shadow-sm">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <UserPlus className="h-4 w-4" />
        Invite reviewer
      </h2>
      <form onSubmit={handleInvite} className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Email address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="expert@example.com"
            className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            User must have logged in at least once.
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Disciplines
          </label>
          <div className="flex flex-wrap gap-2">
            {VALID_DISCIPLINES.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDiscipline(d)}
                className={cn(
                  'rounded border px-3 py-1 text-xs font-medium transition',
                  disciplines.includes(d)
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-slate-300 dark:border-border text-slate-600 dark:text-muted-foreground hover:bg-slate-50 dark:hover:bg-muted',
                )}
              >
                {DISCIPLINE_LABELS[d]}
              </button>
            ))}
          </div>
        </div>
        {status !== 'idle' && (
          <p
            className={cn(
              'text-xs',
              status === 'done' ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400',
            )}
          >
            {message}
          </p>
        )}
        <button
          type="submit"
          disabled={status === 'saving'}
          className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {status === 'saving' ? 'Sending…' : 'Grant reviewer role'}
        </button>
      </form>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Consensus table
// ---------------------------------------------------------------------------

type SortKey = 'rule_name' | 'category' | 'total_reviews' | 'contest_count';

const ConsensusTable: React.FC<{
  items: ConsensusSummary[];
  onResolve: (item: ConsensusSummary) => void;
}> = ({ items, onResolve }) => {
  const [sortKey, setSortKey] = React.useState<SortKey>('contest_count');
  const [asc, setAsc] = React.useState(false);
  const [showOnlyDiscrepancies, setShowOnlyDiscrepancies] = React.useState(false);

  const sorted = React.useMemo(() => {
    let list = showOnlyDiscrepancies ? items.filter((i) => i.has_discrepancy) : items;
    list = [...list].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'string' && typeof vb === 'string')
        return asc ? va.localeCompare(vb) : vb.localeCompare(va);
      return asc ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });
    return list;
  }, [items, sortKey, asc, showOnlyDiscrepancies]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setAsc((v) => !v);
    else { setSortKey(key); setAsc(false); }
  };

  const SortIcon: React.FC<{ k: SortKey }> = ({ k }) =>
    sortKey === k ? (
      asc ? <ChevronUp className="inline h-3 w-3" /> : <ChevronDown className="inline h-3 w-3" />
    ) : null;

  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b px-5 py-3">
        <h2 className="text-sm font-semibold text-muted-foreground">
          Consensus / Discrepancy
        </h2>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-muted-foreground">
          <input
            type="checkbox"
            checked={showOnlyDiscrepancies}
            onChange={(e) => setShowOnlyDiscrepancies(e.target.checked)}
            className="rounded"
          />
          Discrepancies only
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-muted text-slate-500 dark:text-muted-foreground">
            <tr>
              <th
                className="cursor-pointer px-4 py-2 text-left font-medium hover:text-foreground"
                onClick={() => handleSort('rule_name')}
              >
                Rule <SortIcon k="rule_name" />
              </th>
              <th
                className="cursor-pointer px-4 py-2 text-left font-medium hover:text-foreground"
                onClick={() => handleSort('category')}
              >
                Category <SortIcon k="category" />
              </th>
              <th className="px-4 py-2 text-right font-medium">Agree</th>
              <th className="px-4 py-2 text-right font-medium">Optional</th>
              <th className="px-4 py-2 text-right font-medium">Carrier</th>
              <th
                className="cursor-pointer px-4 py-2 text-right font-medium hover:text-foreground"
                onClick={() => handleSort('contest_count')}
              >
                Contest <SortIcon k="contest_count" />
              </th>
              <th className="px-4 py-2 text-right font-medium">Discuss</th>
              <th
                className="cursor-pointer px-4 py-2 text-right font-medium hover:text-foreground"
                onClick={() => handleSort('total_reviews')}
              >
                Total <SortIcon k="total_reviews" />
              </th>
              <th className="px-4 py-2 text-center font-medium">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-slate-400 dark:text-slate-500">
                  No reviews yet.
                </td>
              </tr>
            )}
            {sorted.map((item) => (
              <tr
                key={item.rule_id}
                className={cn(
                  'hover:bg-slate-50 dark:hover:bg-muted',
                  item.has_discrepancy && 'bg-amber-50/40 dark:bg-amber-500/15',
                )}
              >
                <td className="max-w-xs truncate px-4 py-2 font-medium text-muted-foreground">
                  {item.rule_name}
                </td>
                <td className="px-4 py-2 capitalize text-slate-500 dark:text-muted-foreground">{item.category}</td>
                <td className="px-4 py-2 text-right text-green-700 dark:text-green-300">{item.agree_count}</td>
                <td className="px-4 py-2 text-right text-blue-600 dark:text-blue-400">
                  {item.agree_optional_count}
                </td>
                <td className="px-4 py-2 text-right text-sky-600 dark:text-sky-400">
                  {item.agree_carrier_count}
                </td>
                <td className="px-4 py-2 text-right font-semibold text-red-700 dark:text-red-300">
                  {item.contest_count}
                </td>
                <td className="px-4 py-2 text-right text-amber-700 dark:text-amber-300">
                  {item.needs_discussion_count}
                </td>
                <td className="px-4 py-2 text-right text-slate-500 dark:text-muted-foreground">
                  {item.total_reviews}
                </td>
                <td className="px-4 py-2 text-center">
                  {item.has_discrepancy ? (
                    item.discussion_status === 'resolved' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-500/20 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-300">
                        <Check className="h-2.5 w-2.5" />
                        Resolved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
                        <AlertTriangle className="h-2.5 w-2.5" />
                        Open
                      </span>
                    )
                  ) : (
                    <span className="text-slate-300 dark:text-slate-600">—</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {item.has_discrepancy && item.discussion_status !== 'resolved' && (
                    <button
                      type="button"
                      onClick={() => onResolve(item)}
                      className="rounded border border-slate-300 dark:border-border px-2 py-1 text-[10px] font-medium text-slate-600 dark:text-muted-foreground hover:bg-muted"
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Ledger table
// ---------------------------------------------------------------------------

const LedgerTable: React.FC<{ entries: LedgerEntry[] }> = ({ entries }) => (
  <section className="rounded-lg border bg-card shadow-sm">
    <div className="border-b px-5 py-3">
      <h2 className="text-sm font-semibold text-muted-foreground">Contribution Ledger</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 dark:bg-muted text-slate-500 dark:text-muted-foreground">
          <tr>
            <th className="px-4 py-2 text-left font-medium">Reviewer</th>
            <th className="px-4 py-2 text-left font-medium">Disciplines</th>
            <th className="px-4 py-2 text-right font-medium">Total</th>
            <th className="px-4 py-2 text-right font-medium">Agree</th>
            <th className="px-4 py-2 text-right font-medium">Contest</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {entries.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-slate-400 dark:text-slate-500">
                No reviewer activity yet.
              </td>
            </tr>
          )}
          {entries.map((entry) => (
            <tr key={entry.reviewer_id} className="hover:bg-slate-50 dark:hover:bg-muted">
              <td className="px-4 py-2">
                <span className="font-medium text-muted-foreground">
                  {entry.email ?? entry.reviewer_id}
                </span>
              </td>
              <td className="px-4 py-2">
                <div className="flex flex-wrap gap-1">
                  {entry.disciplines.map((d) => (
                    <span
                      key={d}
                      className="rounded-full bg-slate-100 dark:bg-muted px-1.5 py-0.5 text-[10px] capitalize text-slate-600 dark:text-muted-foreground"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-2 text-right font-medium text-muted-foreground">
                {entry.total_reviews}
              </td>
              <td className="px-4 py-2 text-right text-green-700 dark:text-green-300">{entry.agree_count}</td>
              <td className="px-4 py-2 text-right text-red-600 dark:text-red-400">{entry.contest_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// AdminRuleReviewPage
// ---------------------------------------------------------------------------

export default function AdminRuleReviewPage(): React.ReactElement {
  const { roles } = useAuthStore();
  const hasAccess = ADMIN_ROLES.some((r) => roles.includes(r));

  const [consensus, setConsensus] = React.useState<ConsensusSummary[] | null>(null);
  const [ledger, setLedger] = React.useState<LedgerEntry[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resolving, setResolving] = React.useState<ConsensusSummary | null>(null);

  React.useEffect(() => {
    if (!hasAccess) return;
    setLoading(true);
    Promise.all([RuleReviewAPI.adminConsensus(), RuleReviewAPI.adminLedger()])
      .then(([c, l]) => {
        setConsensus(c);
        setLedger(l);
      })
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [hasAccess]);

  const handleResolved = (ruleId: string) => {
    setConsensus((prev) =>
      prev
        ? prev.map((item) =>
            item.rule_id === ruleId
              ? { ...item, discussion_status: 'resolved' }
              : item,
          )
        : prev,
    );
  };

  if (!hasAccess) {
    return (
      <div className="mx-auto max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500 dark:text-amber-400" />
        <h1 className="mt-3 text-lg font-semibold">Admin access required</h1>
        <Navigate to="/" replace />
      </div>
    );
  }

  // Stats
  const openDiscrepancies = consensus?.filter(
    (i) => i.has_discrepancy && i.discussion_status !== 'resolved',
  ).length ?? 0;
  const totalReviews = ledger?.reduce((s, e) => s + e.total_reviews, 0) ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">Rule Review — Admin</h1>
        <p className="text-sm text-muted-foreground">
          {loading
            ? 'Loading…'
            : `${totalReviews} total decisions · ${openDiscrepancies} open discrepancy${openDiscrepancies === 1 ? '' : 'ies'}`}
        </p>
      </header>

      {error && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg border bg-card shadow-sm" />
          ))}
        </div>
      )}

      {!loading && (
        <>
          {consensus && (
            <ConsensusTable
              items={consensus}
              onResolve={(item) => setResolving(item)}
            />
          )}
          {ledger && <LedgerTable entries={ledger} />}
          <InviteReviewerForm />
        </>
      )}

      {resolving && (
        <ResolveModal
          item={resolving}
          onClose={() => setResolving(null)}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
}
